/**
 * Why cart writes are ordered, and not merely sent.
 *
 * `PUT /shop/cart/lines` sets ONE line and answers with the WHOLE cart, and the client used to
 * put whichever answer arrived last into the cache. Nothing serialised the requests, so adding
 * three products quickly put three writes in flight at once, and the responses are three
 * snapshots of the cart taken at three different moments. On a slow link they do not come back
 * in the order they went out: when the second write's answer lands after the third's, the cache
 * is rolled back to a cart that does not have the third product in it.
 *
 * That is exactly what the end-to-end run saw. Five pieces added, the badge reading three, no
 * error anywhere, and the third button pressed again worked — because the row's own `cartQty`
 * had been rolled back with the rest of the cart, which re-enabled its button and made the first
 * press look as though it had never happened. The pieces were never lost on the server: each
 * line is its own upsert (`CartService.setLine`), so the database had all five. Only the screen
 * disagreed, which is worse than an error, because the retailer submits the order they can see.
 *
 * Two things stop it, and both are here so the two cart hooks cannot answer differently:
 *
 *  1. `CART_WRITE_SCOPE` — a TanStack Query mutation scope. Mutations sharing a scope id run one
 *     at a time, across every component that holds the hook, so two cart writes never overlap.
 *     A queued one is still `isPending`, so the button that queued it is disabled until it is
 *     served: a press that cannot be served yet is visibly waiting, never silently dropped.
 *  2. `cartWrites` — a monotonic ticket per write, so an older answer can never be applied over
 *     a newer one even if the scope were lost, a retry reordered them, or a second tab joined
 *     in. The scope stops the race; this makes the stale write unable to do harm.
 *
 * Deliberately NOT the fix: a delay, a debounce, or disabling every button while any write is in
 * flight. The first two make the screen slower without making it right, and the third punishes a
 * retailer for adding two products quickly, which is the thing they came to do.
 */

/** Every write to `['shop','cart']` shares this, so none of them overlap. */
export const CART_WRITE_SCOPE = 'shop-cart';

export interface CartWriteOrder {
  /** A ticket for a write about to be sent. Tickets rise in the order writes start. */
  begin(): number;
  /**
   * Whether this write's answer is still the newest seen — and records it as applied when it is.
   * An answer from a write that started before one already applied is refused.
   */
  accept(ticket: number): boolean;
}

export function cartWriteOrder(): CartWriteOrder {
  let issued = 0;
  let applied = 0;
  return {
    begin: () => {
      issued += 1;
      return issued;
    },
    accept: (ticket) => {
      if (ticket < applied) return false;
      applied = ticket;
      return true;
    },
  };
}

/** The one order for the one cart. */
export const cartWrites = cartWriteOrder();
